// Types for settings and preferences management

export interface CoupleProfile {
  id: string;
  partner1_name: string;
  partner2_name: string;
  created_at: string;
}

export interface UpdateCoupleProfileRequest {
  couple_id: string;
  partner1_name?: string;
  partner2_name?: string;
}

export interface Preferences {
  couple_id: string;
  auto_enroll_challenges: boolean;
  challenge_reminders: boolean;
  partner_challenge_invites: boolean;
  challenge_difficulty: ChallengeDifficulty;
  enable_budget_tracking: boolean;
  expense_alerts: boolean;
  budget_warnings: boolean;
  achievement_notifications: boolean;
  weekly_summary: boolean;
  challenge_updates: boolean;
}

export interface UpdatePreferencesRequest {
  couple_id: string;
  auto_enroll_challenges?: boolean;
  challenge_reminders?: boolean;
  partner_challenge_invites?: boolean;
  challenge_difficulty?: ChallengeDifficulty;
  enable_budget_tracking?: boolean;
  expense_alerts?: boolean;
  budget_warnings?: boolean;
  achievement_notifications?: boolean;
  weekly_summary?: boolean;
  challenge_updates?: boolean;
}

export enum ChallengeDifficulty {
  EASY = 'easy',
  MEDIUM = 'medium',
  HARD = 'hard'
}

export interface BudgetTarget {
  id: string;
  couple_id: string;
  category_id: string;
  category_name: string;
  monthly_limit: number;
  created_at: string;
  updated_at: string;
}

export interface SetBudgetTargetsRequest {
  couple_id: string;
  budgets: Array<{
    category_id: string;
    monthly_limit: number;
  }>;
  total_monthly_budget?: number;
}

export interface ExportDataRequest {
  couple_id: string;
  data_type: ExportDataType;
  date_from?: string;
  date_to?: string;
  format: ExportFormat;
}

export enum ExportDataType {
  EXPENSES = 'expenses',
  CHALLENGES = 'challenges',
  CATEGORIES = 'categories',
  ALL = 'all'
}

export enum ExportFormat {
  CSV = 'csv',
  PDF = 'pdf',
  JSON = 'json'
}

export interface ExportedData {
  filename: string;
  download_url: string;
  expires_at: string;
  file_size: number;
}

// Statistics for data export section
export interface AccountStatistics {
  total_expenses: number;
  days_active: number;
  challenges_completed: number;
  total_points: number;
  partner1_points: number;
  partner2_points: number;
  categories_count: number;
  average_daily_spending: number;
}

// Response types
export interface SettingsResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface PreferencesResponse extends SettingsResponse<Preferences> {}
export interface BudgetTargetsResponse extends SettingsResponse<BudgetTarget[]> {}
export interface ExportDataResponse extends SettingsResponse<ExportedData> {}
export interface StatisticsResponse extends SettingsResponse<AccountStatistics> {}

// Form validation types
export interface CoupleProfileFormData {
  partner1_name: string;
  partner2_name: string;
}

export interface CoupleProfileFormErrors {
  partner1_name?: string;
  partner2_name?: string;
}

export interface BudgetFormData {
  enable_budget_tracking: boolean;
  total_monthly_budget: string;
  category_budgets: Record<string, string>;
}

export interface BudgetFormErrors {
  total_monthly_budget?: string;
  category_budgets?: Record<string, string>;
}

// Validation constants
export const SETTINGS_VALIDATION = {
  MIN_NAME_LENGTH: 1,
  MAX_NAME_LENGTH: 100,
  MIN_BUDGET_AMOUNT: 0,
  MAX_BUDGET_AMOUNT: 999999,
  MAX_EXPORT_MONTHS: 12,
} as const;

// Default preferences for new couples
export const DEFAULT_PREFERENCES: Omit<Preferences, 'couple_id'> = {
  auto_enroll_challenges: true,
  challenge_reminders: true,
  partner_challenge_invites: true,
  challenge_difficulty: ChallengeDifficulty.MEDIUM,
  enable_budget_tracking: false,
  expense_alerts: true,
  budget_warnings: true,
  achievement_notifications: true,
  weekly_summary: false,
  challenge_updates: true,
};